import { useState, useEffect } from "react";
import { Radio } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUp, faArrowDown } from "@fortawesome/free-solid-svg-icons";
import "./InfluencersSort.scss";
import { BaseFilterProps } from "interfaces/filter-component-base";

type SortValue = {
  sortField: "engagement" | "followers";
  sortDirection: "asc" | "desc";
};

const sortOptions: {
  label: string;
  value: SortValue;
  description: string;
  icon: any;
  key: string;
}[] = [
  {
    label: "Highest to Lowest",
    value: { sortField: "engagement", sortDirection: "desc" },
    description: "Best engagement first",
    icon: faArrowUp,
    key: "engagement_high_to_low",
  },
  {
    label: "Lowest to Highest",
    value: { sortField: "engagement", sortDirection: "asc" },
    description: "Lower engagement first",
    icon: faArrowDown,
    key: "engagement_low_to_high",
  },
  {
    label: "Highest to Lowest",
    value: { sortField: "followers", sortDirection: "desc" },
    description: "Most followers first",
    icon: faArrowUp,
    key: "followers_high_to_low",
  },
  {
    label: "Lowest to Highest",
    value: { sortField: "followers", sortDirection: "asc" },
    description: "Fewer followers first",
    icon: faArrowDown,
    key: "followers_low_to_high",
  },
];

const getKeyFromValue = (val?: SortValue) => {
  if (!val) return undefined;
  if (val.sortField === "engagement" && val.sortDirection === "desc")
    return "engagement_high_to_low";
  if (val.sortField === "engagement" && val.sortDirection === "asc")
    return "engagement_low_to_high";
  if (val.sortField === "followers" && val.sortDirection === "desc")
    return "followers_high_to_low";
  if (val.sortField === "followers" && val.sortDirection === "asc")
    return "followers_low_to_high";
  return undefined;
};

const getValueFromKey = (key: string): SortValue | undefined => {
  const found = sortOptions.find((opt) => opt.key === key);
  return found ? found.value : undefined;
};

const InfluencersSort = (props: BaseFilterProps) => {
  const { onChangeValues, values } = props;
  const [selectedKey, setSelectedKey] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (
      values &&
      typeof values === "object" &&
      values.sortField &&
      values.sortDirection
    ) {
      setSelectedKey(getKeyFromValue(values));
    } else {
      setSelectedKey(undefined);
    }
  }, [values]);

  const handleSortChange = (e: any) => {
    const key = e.target.value;
    setSelectedKey(key);
    const sortValue = getValueFromKey(key);
    if (onChangeValues && sortValue) {
      onChangeValues(sortValue);
    }
  };

  return (
    <div className="influencers-sort-items">
      <Radio.Group
        value={selectedKey}
        onChange={handleSortChange}
        className="influencers-sort-group"
      >
        <div className="influencers-sort-section">
          <p className="influencers-sort-section-title">ENGAGEMENT RATE</p>
          {sortOptions
            .filter((opt) => opt.value.sortField === "engagement")
            .map((opt) => (
              <label className="influencers-sort-item" key={opt.key}>
                <div
                  className={`influencers-sort-icon engagement-${
                    opt.value.sortDirection === "desc" ? "high" : "low"
                  }`}
                >
                  <FontAwesomeIcon icon={opt.icon} />
                </div>
                <div className="influencers-sort-content">
                  <p className="influencers-sort-content-title">{opt.label}</p>
                  <p className="influencers-sort-content-description">
                    {opt.description}
                  </p>
                </div>
                <div className="influencers-sort-radio">
                  <Radio value={opt.key} />
                </div>
              </label>
            ))}
        </div>
        <div className="influencers-sort-section">
          <p className="influencers-sort-section-title">FOLLOWERS</p>
          {sortOptions
            .filter((opt) => opt.value.sortField === "followers")
            .map((opt) => (
              <label className="influencers-sort-item" key={opt.key}>
                <div
                  className={`influencers-sort-icon followers-${
                    opt.value.sortDirection === "desc" ? "high" : "low"
                  }`}
                >
                  <FontAwesomeIcon icon={opt.icon} />
                </div>
                <div className="influencers-sort-content">
                  <p className="influencers-sort-content-title">{opt.label}</p>
                  <p className="influencers-sort-content-description">
                    {opt.description}
                  </p>
                </div>
                <div className="influencers-sort-radio">
                  <Radio value={opt.key} />
                </div>
              </label>
            ))}
        </div>
      </Radio.Group>
    </div>
  );
};

export default InfluencersSort;
